/* eslint-disable */
import type { AnalyticsMessage } from "../cash/cdpproxy/api/analytics_message.pb";
import type {
  CardOrderEventCardOrderState,
  CardOrderEventCardOrderStateReason,
  IssuedCardEventCardIssuer,
} from "../cash/postcard/events.pb";
import type { Money } from "../common/currency.pb";
import type { InstrumentType } from "../common/instrument.pb";

/** Authenticated request to get data used to build URLs for Trustpilot Business Generated Links. */
export interface GetTrustpilotUrlPayloadRequest {
  utm_source?: string;
}

/**
 * Returns PII of the customer encrypted in a format that matches the
 * Trustpilot business generated link strategy.  `urlEncoded(base64(encrypted(payload)))`
 * https://support.trustpilot.com/hc/en-us/articles/115004145087-Send-invitations-with-Business-Generated-Links-
 */
export interface GetTrustpilotUrlPayloadResponse {
  payload?: string;
}

/**
 * API request facet types should match the ones defined here:
 * https://github.com/squareup/cash-server/blob/main/caps/facets/src/main/proto/squareup/cash/caps/types/types.proto
 */
export interface GetProfileInfoRequest {
  facets?: GetProfileInfoRequestFacetType[];
}

export enum GetProfileInfoRequestFacetType {
  BITCOIN_BALANCE = "BITCOIN_BALANCE",
  INVESTING_PORTFOLIO_SUMMARY = "INVESTING_PORTFOLIO_SUMMARY",
  ISSUED_CASH_CARD = "ISSUED_CASH_CARD",
  SPONSORSHIP = "SPONSORSHIP",
}

/**
 * API response objects mirror the ones defined here and may be more limited (i.e. they may not include all fields):
 * https://github.com/squareup/cash-server/blob/main/caps/facets/src/main/proto/squareup/cash/caps/types/types.proto
 */
export interface GetProfileInfoResponse {
  bitcoin_balance?: GetProfileInfoResponseBitcoinBalance;
  investing_portfolio_summary?: GetProfileInfoResponseInvestingPortfolioSummary;
  issued_cash_card?: GetProfileInfoResponseIssuedCashCard;
  sponsorship?: GetProfileInfoResponseSponsorship;
}

export interface GetProfileInfoResponseBitcoinBalance {
  snapshot?: Money;
}

export interface GetProfileInfoResponseInvestingPortfolioSummary {
  /** Has the customer ever held stock? */
  has_portfolio?: boolean;
  /** Is the customer actively holding stock? */
  has_holdings?: boolean;
}

export interface GetProfileInfoResponseIssuedCashCard {
  /** A token identifying this issued card. */
  token?: string;
  /** The last four digits of the pan. */
  last_four?: string;
  /** The type of payment instrument, such as Visa or MasterCard. */
  instrument_type?: InstrumentType;
  /** The name of the cardholder. Right now, this will always be the customer's full name. */
  cardholder_name?: string;
  /** True if the customer has completed activation, which must occur before first use. */
  activated?: boolean;
  /** True if the card is a virtual card. False if the card is a physical card. */
  virtual?: boolean;
  /**
   * True if the caller must verify a passcode or passcode token to view the card number,
   * expiration and CVV.
   */
  locked?: boolean;
  /**
   * Whether this card has been locked by a sponsor.
   * https://docs.google.com/document/d/1TR5wTRy1LHS1Js8N-QgS2zjcbcsdwwrNgXb2GiTWJ54/edit?usp=sharing
   */
  sponsor_locked?: boolean;
  /** The entity responsible for issuing the card */
  issuer?: IssuedCardEventCardIssuer;
  /** When the card was issued */
  issued_at?: number;
  /** Information pertaining to the order placed for the physical Cash card. */
  card_order_data?: GetProfileInfoResponseIssuedCashCardCardOrderData;
}

export interface GetProfileInfoResponseIssuedCashCardCardOrderData {
  state?: CardOrderEventCardOrderState;
  state_reason?: CardOrderEventCardOrderStateReason;
  state_transitioned_at?: number;
  issued_at?: number;
  flow_type?: string;
  last_card_ordered_at?: number;
  name_on_card?: string;
  card_theme_token?: string;
}

export interface GetProfileInfoResponseSponsorship {
  /** Indicates whether this person should be considered sponsored */
  is_sponsored?: boolean;
}

export interface TriggerCampaignRequest {
  id?: TriggerCampaignRequestId;
  variables?: TriggerCampaignRequestVariable[];
  client_route_url?: string;
}

export enum TriggerCampaignRequestId {
  REPLACE_CARD = "REPLACE_CARD",
  VERIFY_ADDRESS = "VERIFY_ADDRESS",
  CARD_DRAWER = "CARD_DRAWER",
  NEW_CARD = "NEW_CARD",
  PAYCHECKS = "PAYCHECKS",
  ACTIVATE_CARD = "ACTIVATE_CARD",
  WEB_UNSUPPORTED_CONTINUE_IN_MOBILE = "WEB_UNSUPPORTED_CONTINUE_IN_MOBILE",
}

export interface TriggerCampaignRequestVariable {
  name?: string;
  value?: string;
  type?: TriggerCampaignRequestVariableType;
}

export enum TriggerCampaignRequestVariableType {
  STRING = "STRING",
}

export interface TriggerCampaignResponse {
}

/** Looks up experiment values for a namespace. These are defined in Experiments.java. */
export interface GetExperimentsRequest {
  namespace?: string;
}

/**
 * The results are a map of experiment keys to their assignment values.
 * The key is the experiment key, the value is the experiment assignment value.
 */
export interface GetExperimentsResponse {
  experiments?: { [key: string]: string };
}

export interface GetExperimentsResponseExperimentsEntry {
  key: string;
  value: string;
}

export interface CdpTrackEventRequest {
  analytics_messages?: AnalyticsMessage[];
}

export interface CdpTrackEventResponse {
}

export interface GetReceiptRequest {
  token?: string;
  source?: string;
}

export interface GetReceiptResponse {
  payment_history_data?: string;
}

/** Request to refresh cookies - empty request body. */
export interface CookieRefreshRequest {
}

/** Response for cookie refresh operation. */
export interface CookieRefreshResponse {
}

/** The CashFrontendAppService provides publicly accessible RPC endpoints for web clients. */
export interface CashFrontendAppService {
  GetTrustpilotUrlPayload(request: GetTrustpilotUrlPayloadRequest): Promise<GetTrustpilotUrlPayloadResponse>;
  GetProfileInfo(request: GetProfileInfoRequest): Promise<GetProfileInfoResponse>;
  TriggerCampaign(request: TriggerCampaignRequest): Promise<TriggerCampaignResponse>;
  GetExperiments(request: GetExperimentsRequest): Promise<GetExperimentsResponse>;
  CdpTrackEvent(request: CdpTrackEventRequest): Promise<CdpTrackEventResponse>;
  GetReceipt(request: GetReceiptRequest): Promise<GetReceiptResponse>;
  CookieRefresh(request: CookieRefreshRequest): Promise<CookieRefreshResponse>;
}
